import * as _ from "lodash";
import queryString from "query-string";

import { swSettings } from "app/common/services/swSettings";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import { commonWebSources } from "app/components/filters-bar/utils";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import type { IKeywordsCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import type { AvaliableDurations } from "../utils/dashboardWizardDefaultState";
import {
    getComponentProperties,
    getComponentByWebSource,
    getMetricProperties,
    getProductClaims,
    getMetricWidgetTypes,
    getMetricWidgetFilters,
    getPeriodOverPeriodParams,
    getWidgetWidth,
    getWebsiteHeaderData,
    getScrapingConfsIDsAsString,
} from "../utils/dashboardWizardUtils";

import * as DashboardWizardActionTypes from "./dashboardWizardActionTypes";
//todo: rename to defaultActionParams
//todo: check if reducer really need all the returning values and if not divide to separate functions
const defaultStateParams = (customDashboardState, webSource?, metric?, type?, key?, family?, duration?) => {
    const _metric = metric || customDashboardState.widget.metric;
    const _webSource = webSource || customDashboardState.widget.webSource;
    const _key = key || customDashboardState.widget.key;
    const _family = family || customDashboardState.widget.family;
    const _duration = duration || customDashboardState.widget.duration;
    const _type = type || customDashboardState.widget.type || customDashboardState.widget.widgetType;
    const _componentName = getComponentByWebSource(customDashboardState, _webSource, _metric, _family, _key);

    let _isCompare =
        key && Object.keys(key).length > 0
            ? key.length > 1
            : customDashboardState.widget.key
              ? customDashboardState.widget.key.length > 1
              : false;

    const _metricProps = getMetricProperties(_metric, _family);
    if (_metricProps.noCompare) {
        _isCompare = false;
    }

    const _componentProps = getComponentProperties(_componentName, _metricProps, _key);

    const _productClaims = getProductClaims();
    const _metricWidgetTypes = getMetricWidgetTypes(
        _metric,
        _isCompare,
        customDashboardState,
        _metricProps.compareOnly,
        key,
    );
    const _metricWidgetFilters = getMetricWidgetFilters(
        _metric,
        _family,
        _type,
        _isCompare,
        customDashboardState,
        _metricProps.compareOnly,
    );
    const _periodOverPeriodParams = getPeriodOverPeriodParams(_family, _metric, _key, _duration, _componentName);
    const _widgetWidth = getWidgetWidth(_type);

    return {
        component: _componentName,
        componentProps: _componentProps,
        metricProps: _metricProps,
        productClaims: _productClaims,
        metricWidgetTypes: _metricWidgetTypes,
        metricWidgetFilters: _metricWidgetFilters,
        periodOverPeriodParams: _periodOverPeriodParams,
        width: _widgetWidth,
        key: _key,
    };
};

export const dashboardWizardIsEditing = (isEditing: boolean, editedWidget?: IWidget<any>) => ({
    type: DashboardWizardActionTypes.DASHBOARD_WIZARD_WIDGET_IS_EDITING,
    isEditing,
    editedWidget,
});

export const dashboardWizardMetricAndFamilyChanged = (customDashboardState, metric, family, key) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_METRIC_FAMILY_CHANGED,
        metric,
        family,
        key,
        ...defaultStateParams(customDashboardState, null, metric, null, key, family),
    };
};

export const dashboardWizardKeyChanged = (customDashboardState, key) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_KEY_CHANGED,
        key,
        isCompare: key.length > 1,
        ...defaultStateParams(customDashboardState, null, null, null, key),
    };
};

const getWebSourceForCampaigns = (selectedCampaign: IKeywordsCampaignData) => {
    const isAllDevicesSameAsItem = selectedCampaign?.scrapingConfigurations?.every(
        (sc) => sc.device === selectedCampaign?.scrapingConfigurations[0].device,
    );
    const isMobileEnabled = selectedCampaign?.scrapingConfigurations?.some(
        (sc) => sc.device === DEVICES_TYPES_FOR_API.MOBILE,
    );
    if (!isAllDevicesSameAsItem && isMobileEnabled) {
        return commonWebSources.mobileWeb().id;
    }
    if (isAllDevicesSameAsItem) {
        return selectedCampaign?.scrapingConfigurations[0].device === DEVICES_TYPES_FOR_API.MOBILE
            ? commonWebSources.mobileWeb().id
            : commonWebSources.desktop().id;
    }
};

export const dashboardWizardCurrentCampaignChanged = (
    customDashboardState,
    selectedCampaign: IKeywordsCampaignData,
    initialKey = undefined,
    duration: string,
) => {
    const webSource = getWebSourceForCampaigns(selectedCampaign);
    const country = !webSource
        ? null
        : getScrapingConfsIDsAsString(selectedCampaign?.scrapingConfigurations, webSource);
    // initialKey param allows the initial state of the widget to be in compare mode
    // which is necessary for campaigns where the assets being added are not websites
    // but in some widgets we still require all the sites connected to the campaign to
    // be included in the keys.
    const key = initialKey ?? [
        {
            id: selectedCampaign.site,
            name: selectedCampaign.site, //used in the subtitle
            image: selectedCampaign.asset,
        },
    ];
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_CURRENT_CAMPAIGN_CHANGED,
        selectedCampaign,
        ...defaultStateParams(customDashboardState, null, null, null, null, EFamilyTypes.RankTracker, null),
        isCompare: key?.length > 1,
        key,
        duration,
        country,
        webSource,
    };
};

export const dashboardWizardNewWidget = () => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_WIDGET_NEW,
    };
};

export const dashboardWizardSetWidgetId = (id: string) => ({
    type: DashboardWizardActionTypes.DASHBOARD_WIZARD_WIDGET_SET_ID,
    id,
});

export const dashboardWizardKeyAppend = (customDashboardState, key) => {
    // being able to pass in an array allows the initial state to be compare
    // for use cases that don't add competitors manually (example is rank tracker overview report performance graph)
    const keyArray = Array.isArray(key) ? key : [key];
    const _newKey = [...customDashboardState.widget.key, ...keyArray];
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_KEY_APPEND,
        family: customDashboardState?.widget?.family,
        key: _newKey,
        isCompare: customDashboardState.widget.key.length + 1 > 1,
        ...defaultStateParams(customDashboardState, null, null, null, _newKey),
    };
};

export const dashboardWizardKeyRemove = (customDashboardState, key) => {
    const _newKey = customDashboardState.widget.key.filter((item) => {
        if (item) {
            if (item.id && item.id !== key.id) {
                return key;
            }
            if (item.name && item.name !== key.name) {
                return key;
            }
        }
    });
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_KEY_REMOVE,
        key: _newKey,
        isCompare: customDashboardState.widget.key.length - 1 > 1,
        ...defaultStateParams(customDashboardState, null, null, null, _newKey),
    };
};

export const dashboardWizardKeyReset = (customDashboardState) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_KEY_RESET,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardWebSourceChanged = (customDashboardState, webSource) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_WEBSOURCE_CHANGED,
        webSource,
        ...defaultStateParams(customDashboardState, webSource),
    };
};

export const dashboardWizardAvaliableDurationChanged = (availableDurations: AvaliableDurations[]) => ({
    type: DashboardWizardActionTypes.DASHBOARD_WIZARD_AVALIABLE_DURATION_CHANGED,
    availableDurations,
});

export const dashboardWizardSetDisabledDuration = (disabledDurations: string[]) => ({
    type: DashboardWizardActionTypes.DASHBOARD_WIZARD_SET_DIABLE_DURATION,
    disabledDurations,
});

export const dashboardWizardSetAvaliableCountries = (avaliableCountries: string[]) => ({
    type: DashboardWizardActionTypes.DASHBOARD_WIZARD_SET_AVALIABLE_COUNTRIES,
    avaliableCountries,
});

export const dashboardWizardSetAvaliableSource = (customDashboardState, country: string) => ({
    type: DashboardWizardActionTypes.DASHBOARD_WIZARD_SET_AVALIABLE_SOURCE,
    country,
    metric: customDashboardState?.widget?.metric,
    family: customDashboardState?.widget?.family,
    ...defaultStateParams(customDashboardState),
});

export const dashboardWizardCountryChanged = (customDashboardState, country) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_COUNTRY_CHANGED,
        country,
        metric: customDashboardState?.widget?.metric,
        desktopMobileWebTotalCountries: getComponentProperties(
            "DesktopMobileWebTotal",
            customDashboardState.metricProps,
        ).countries,
        family: customDashboardState?.widget?.family,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardDurationChanged = (customDashboardState, duration, comparedDuration) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_DURATION_CHANGED,
        duration,
        comparedDuration,
        ...defaultStateParams(customDashboardState, null, null, null, null, null, duration),
    };
};

export const dashboardWizardDeviceChanged = (customDashboardState, device) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_DEVICE_CHANGED,
        device,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardWidgetTypeChanged = (customDashboardState, widgetType) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_WIDGET_TYPE_CHANGED,
        widgetType,
        ...defaultStateParams(customDashboardState, null, null, widgetType.type),
    };
};

const dashboardWizardComparedDurationChanged = (customDashboardState, comparedDuration) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_COMPARED_DURATION_CHANGED,
        comparedDuration,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardWidgetChanged = (customDashboardState, widgetModel) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_WIDGET_CHANGED,
        ...widgetModel,
        isCompare: widgetModel.key.length > 1,
        desktopMobileWebTotalCountries: getComponentProperties(
            "DesktopMobileWebTotal",
            customDashboardState.metricProps,
        ).countries,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardFilterChanged = (customDashboardState, filter) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_FILTER_CHANGED,
        filter,
        ...defaultStateParams(customDashboardState),
    };
};

export const segmentsGroupIdChanged = (customDashboardState, segmentsGroupId: string) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_FILTER_SEGMENTS_GROUP_ID_CHANGED,
        segmentsGroupId,
        ...defaultStateParams(customDashboardState),
    };
};

export const categoryIdChanged = (customDashboardState, categoryId: string) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_FILTER_SELECTED_CATEGORY_ID_CHANGED,
        categoryId,
        ...defaultStateParams(customDashboardState),
    };
};

export const selectedSegmentsIdChanged = (customDashboardState, selectedSegmentsId) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_FILTER_SELECTED_SEGMENTS_ID_CHANGED,
        selectedSegmentsId,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardSelectedChannelChanged = (customDashboardState, selectedChannel) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_SELECTED_CHANNEL_CHANGED,
        selectedChannel,
        ...defaultStateParams(customDashboardState),
    };
};

export const dashboardWizardDataModeChanged = (customDashboardState, dataMode) => {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_DATAMODE_CHANGED,
        dataMode,
        ...defaultStateParams(customDashboardState),
    };
};

//Async flow for GetMetricMetaData.
function receiveMetricMetaDataSuccess(customDashboardState, countries, filters) {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_RECEIVE_METRIC_META_DATA_SUCCESS,
        countries,
        filters,
        ...defaultStateParams(customDashboardState),
    };
}

function receiveMetricMetaDataFail(customDashboardState, error) {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_RECEIVE_METRIC_META_DATA_ERROR,
        error,
        ...defaultStateParams(customDashboardState),
    };
}

function requestMetricMetaData(customDashboardState) {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_REQUEST_METRIC_META_DATA,
        ...defaultStateParams(customDashboardState),
    };
}

function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export const fetchMetricMetaData = (customDashboardState, keys) => {
    const SWSettings: any = swSettings;
    const fetchService = DefaultFetchService.getInstance();
    let keyForAPI = keys.map((k) => {
        return k.id;
    });
    keyForAPI = keyForAPI.join();
    const params = {
        from: DurationService.getDurationData(
            customDashboardState.widget.duration,
            null,
            customDashboardState.component,
        ).forAPI["from"],
        to: DurationService.getDurationData(customDashboardState.widget.duration, null, customDashboardState.component)
            .forAPI["to"],
        keys: keyForAPI,
        store: capitalizeFirstLetter(keys[0].store),
        metric: customDashboardState.widget.metric,
        isDaily: customDashboardState.widget.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
    };
    const stateParams = defaultStateParams(customDashboardState) as any;
    const apiController = stateParams.metricProps.apiController;
    const metaDataApiController = stateParams.metricProps.metaDataApiController;
    let url;
    if (metaDataApiController) {
        url =
            `/widgetApi/${metaDataApiController}/${metaDataApiController}/MetricMetaData?` +
            queryString.stringify({ ...params, metric: metaDataApiController });
    } else {
        url =
            `/widgetApi/${typeof apiController === "function" ? apiController(keys) : apiController}/${
                params.metric
            }/MetricMetaData?` + queryString.stringify(params);
    }

    return (dispatch) => {
        dispatch(requestMetricMetaData(customDashboardState));
        return fetchService
            .get<any>(url)
            .then(
                // On success return response.json().
                (response) => response,
                // On error dispatch error action.
                (error) => dispatch(receiveMetricMetaDataFail(customDashboardState, error)),
            )
            .then((response) => {
                const countries = SWSettings.filterCountries(response.settings.countries);
                return dispatch(receiveMetricMetaDataSuccess(customDashboardState, countries, response.filters));
            });
    };
};

function receiveGaVerifiedDataSuccess(customDashboardState, isGAVerifiedSupported) {
    return {
        type: DashboardWizardActionTypes.DASHBOARD_WIZARD_RECEIVE_GA_VERIFIED_SUCCESS,
        isGAVerifiedDataSupported: isGAVerifiedSupported,
        ...defaultStateParams(customDashboardState),
    };
}

export const updateGAVerifiedDataFilter = (customDashboardState, key) => {
    if (key.length < 1) {
        return (dispatch) => {
            return dispatch({
                type: DashboardWizardActionTypes.DASHBOARD_WIZARD_NO_KEYS,
                ...defaultStateParams(customDashboardState),
            });
        };
    }
    return (dispatch) => {
        return getWebsiteHeaderData(key).then((res) => {
            return dispatch(receiveGaVerifiedDataSuccess(customDashboardState, !!_.find(res, { hasGaToken: true })));
        });
    };
};
